import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useStateIfMounted } from 'use-state-if-mounted';
import debounce from 'lodash.debounce';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
  styled,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon, Menu as MenuIcon } from '@mui/icons-material';

import searchService, { searchCache } from 'services/search';

import { NAVBAR_MENUS, NAV_MENUS } from 'constants/index';
import useTopValue from 'hooks/useTopValue';
import useWalletContext from 'hooks/useWalletContext';
import { openLink } from 'utils/openLink';

import { /*ToolTip,*/ GMILogo } from 'components/StyledComponents';
// import { DarkIcon, LightIcon } from 'components/ImageComponent';
import {
  WalletSidebar,
  SideNavigation,
  AccountSettingsSidebar,
  SubMenu,
  SearchResultContainer,
  AddressButton,
  NetworkSelect,
  SearchDrawer,
} from './components';

/********************  Styled Components  ********************/
const Root = styled('div')(({ top }) => ({
  height: 60,
  width: '100%',
  zIndex: 1300,
  position: 'fixed',
  top: top,

  '@media (max-width: 1439px)': {
    transform: 'scale(0.8)',
    transformOrigin: '0 0',
    width: 'calc(100% / 0.8)',
  },
  '@media (max-width: 1279px)': {
    transform: 'none',
    width: '100%',
  },
  '@media (max-width: 599px)': {
    height: 50,
  },
}));

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '100%',
  width: '100%',
  padding: theme.spacing(0, 1.6),
  backgroundColor: theme.palette.background.secondary,
  justifyContent: 'space-between',
  borderBottom: `1px solid ${theme.palette.border.separator}`,

  '& > div, & > a': {
    padding: theme.spacing(0, 1.6),
  },
  '@media (max-width: 1439px)': {
    padding: theme.spacing(0, 4),
  },
  '@media (max-width: 1279px)': {
    padding: theme.spacing(0, 3),
  },
  '@media (max-width: 599px)': {
    padding: theme.spacing(0, 1),
  },
}));

const SearchFieldContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: 'relative',
  padding: '0 !important',
  marginLeft: theme.spacing(3),
  marginRight: theme.spacing(2),
  width: '100%',
}));

const SearchTextField = styled(TextField)(({ theme }) => ({
  background: theme.palette.background.secondary,
  flex: 1,
  letterSpacing: '0.25px',
  width: '100%',
  height: 37,
  '& fieldset': {
    border: `1px solid ${theme.palette.border.decorative}`,
    borderRadius: 8,
  },
  '& input::placeholder': {
    fontSize: 16,
    fontWeight: 400,
    color: theme.palette.text.secondary,
    opacity: 1,

    '@media (max-width: 1023px)': {
      fontSize: 13,
    },
  },
  '& input': {
    color: theme.palette.text.main,
    fontSize: 16,
    fontWeight: 400,
    padding: 0,
    width: '100%',
    height: 37,

    '@media (max-width: 1023px)': {
      fontSize: 13,
    },
    '@media (max-width: 904px)': {
      fontSize: 16,
    },
  },
  '&:has(input:focus-within) + div': {
    display: 'block',
  },

  '&:hover': {
    background: theme.palette.background.apply.hovered,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.border.decorative,
    },
  },
  '&:active': {
    background: theme.palette.background.apply.pressed,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.border.decorative,
    },
  },
}));

const Menu = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  paddingRight: '0px !important',
  marginRight: '-5px',

  '@media (max-width: 904px)': {
    display: 'none',
  },
}));

const SideNavigationButton = styled(IconButton)(() => ({
  display: 'none',

  '@media (max-width: 904px)': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 0,
    marginTop: 5,
  },
}));

/*
const IconContainer = styled('div')(({ theme }) => ({
  margin: theme.spacing(0, 0.5),
  padding: '0 !important',

  '& svg': {
    marginBottom: -5,
  },
  '&:hover': {
    background: theme.palette.background.apply.hovered,
    borderRadius: '50%',
  },
  '&:active': {
    background: theme.palette.background.apply.pressed,
    borderRadius: '50%',
  },
}));
*/

const CustomMenuButton = styled('a')(({ theme, active }) => ({
  height: 46,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  fontWeight: '600',
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  border: active === 'true' ? `3px solid ${theme.palette.link.primary}` : 'none',
  padding: theme.spacing(0.5, 1),
  height: 'auto',
  color: active === 'true' ? theme.palette.link.primary : theme.palette.text.main,
  textDecoration: 'none',

  '@media (max-width: 1023px)': {
    fontSize: '14px',
  },
}));

const GetGMIButton = styled('a')(({ theme }) => ({
  height: 46,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  fontSize: '16px',
  lineHeight: '24px',
  letterSpacing: '0.15px',
  backgroundColor: '#00AB58',
  color: '#FFFFFF',
  padding: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  height: 'auto',
  fontWeight: 600,
  border: 'none',
  textDecoration: 'none',

  '&:hover': {
    backgroundColor: '#009649',
  },

  '&:active': {
    backgroundColor: '#008040',
  },

  '@media (max-width: 1023px)': {
    fontSize: '14px',
  },
}));

const ToggleContainer = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.border.separator}`,
  height: 38,
  margin: theme.spacing(0, 1),
  padding: '0 !important',
  overflow: 'hidden',

  '@media (max-width: 599px)': {
    margin: theme.spacing(0),
    marginRight: theme.spacing(1),
  },
}));

const MobileMenuBox = styled(Box)(({ theme }) => ({
  padding: '0px !important',
  '@media (max-width: 904px)': {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'start',
    gap: theme.spacing(1),

    '@media (max-width: 904px)': {
      '& a': {
        marginTop: -3,
      },
    },
  },
}));

const MobileCtaBtnContainer = styled(Box)(({ theme }) => ({
  padding: '0px !important',
  margin: theme.spacing(0),
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'flex-end',
}));

/********************  Main Component  ********************/
const Header = ({ toggleTheme }) => {
  const theme = useTheme();
  const at1280 = useMediaQuery('(max-width: 1280px)');
  const below600 = useMediaQuery('(max-width: 599px)');

  const location = useLocation();
  const history = useHistory();
  const { fixedTopPosition } = useTopValue();
  const { globalChain } = useWalletContext();

  const [mobileNavOpen, setMobileNavOpen] = useStateIfMounted(false);
  const [walletSidebarOpen, setWalletSidebarOpen] = useStateIfMounted(false);
  const [settingMenu, setSettingMenu] = useStateIfMounted(false);
  const [isLoading, setIsLoading] = useStateIfMounted(false);
  const [error, setError] = useStateIfMounted(false);
  const [searchText, setSearchText] = useStateIfMounted('');
  const [searchResult, setSearchResult] = useStateIfMounted({});
  const [mobileSearch, setMobileSearch] = useStateIfMounted(false);
  const [isInputFocused, setIsInputFocused] = useStateIfMounted(false);
  const searchContainerRef = React.useRef(null);

  // Search optimization refs
  const searchAbortControllerRef = useRef(null);

  const { pathname, search } = location;

  const HeaderCtaBtnContainer = below600 ? MobileCtaBtnContainer : React.Fragment;

  const latestQueryRef = useRef('');

  const handleSearch = useCallback(async (input) => {
    // Cancel any in-flight request
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    setSearchText(input);

    // Clear results if empty search
    if (!input || input.trim().length === 0) {
      setSearchResult({});
      setIsLoading(false);
      return;
    }

    // Do not hit network for < 3 chars; rely on cache-only flow
    if (input.trim().length < 3) {
      setIsLoading(false);
      return;
    }

    // Check cache first (exact match)
    const cacheKey = input.toLowerCase().trim();
    let cachedResult = searchCache.getExactFresh(cacheKey);

    // If no exact match, check for prefix matches
    if (!cachedResult && cacheKey.length >= 2) {
      cachedResult = searchCache.getPrefixFilteredFresh(cacheKey);
    }

    if (cachedResult) {
      setSearchResult(cachedResult);
      setIsLoading(false);
      return;
    }

    // Create new abort controller
    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;

    latestQueryRef.current = input;

    setIsLoading(true);
    setError(false);

    try {
      const response = await searchService.search(input, abortController.signal);

      if (!abortController.signal.aborted && latestQueryRef.current === input) {
        // Cache the result
        searchCache.set(cacheKey, response);

        setSearchResult(response);
        setIsLoading(false);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Search error:', err);
        setError(true);
        setIsLoading(false);
      }
    } finally {
      if (searchAbortControllerRef.current === abortController) {
        searchAbortControllerRef.current = null;
      }
    }
  }, []);

  const debouncedApiCall = useMemo(
    () => debounce(handleSearch, 450), // Only API calls are debounced
    [handleSearch],
  );

  // Immediate cache check + debounced API call
  const handleSearchInput = useCallback(
    (input) => {
      setSearchText(input);

      // Clear results if empty search
      if (!input || input.trim().length === 0) {
        setSearchResult({});
        setIsLoading(false);
        return;
      }

      // Check cache immediately for instant results
      const cacheKey = input.toLowerCase().trim();
      let cachedResult = searchCache.getExactFresh(cacheKey);

      // If no exact match, check for prefix matches
      if (!cachedResult && cacheKey.length >= 2) {
        cachedResult = searchCache.getPrefixFilteredFresh(cacheKey);
      }

      if (cachedResult) {
        // Show cached results immediately
        setSearchResult(cachedResult);
        setIsLoading(false);
      } else if (input.trim().length >= 3) {
        // No cache hit, trigger debounced API call
        setIsLoading(true);
        debouncedApiCall(input);
      } else {
        // For < 3 chars with no cache, avoid network and stop loading
        setIsLoading(false);
      }
    },
    [debouncedApiCall],
  );

  const handleCloseSideModals = () => {
    setWalletSidebarOpen(false);
    if (settingMenu) {
      setMobileNavOpen(true);
    }
    if (!walletSidebarOpen) {
      setMobileNavOpen(!mobileNavOpen);
      setSettingMenu(false);
    }
  };

  const goPath = (path) => {
    // If navigating to launchpad, append the current chain as a query parameter
    if (path.includes('launchpad')) {
      const url = new URL(path);
      url.searchParams.set('chain', globalChain);
      path = url.toString();
    }

    // If navigating to LP Bonds root page, keep URL stable and do not mutate stored chain

    path.startsWith('https') ? openLink(path, true) : history.push(path);
  };

  const isActiveSubMenu = (submenus) => {
    if (pathname === '/') {
      return false;
    }

    return submenus.some((submenu) => {
      if (Array.isArray(submenu.tabs)) {
        return submenu.tabs.some((tab) => tab === pathname + search);
      }
      return false;
    });
  };

  const isActiveTab = (tabs) => {
    // Special handling for root path - only active when exactly on root
    if (tabs.includes('/')) {
      return pathname === '/';
    }
    return tabs.some((tab) => pathname.startsWith(tab));
  };

  const showSearchResultContainer = useMemo(
    () => searchText && searchText.length > 2,
    [searchText],
  );

  const handleClickOutside = React.useCallback((event) => {
    if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
      setIsInputFocused(false);
      setSearchText('');
      setSearchResult({});
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  useEffect(() => {
    if (pathname.includes('account/settings')) {
      setSettingMenu(true);
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      <AccountSettingsSidebar
        setWalletSidebarOpen={setWalletSidebarOpen}
        open={at1280 && settingMenu}
        setSettingMenuOpen={(val) => {
          setSettingMenu(val);
        }}
      />
      <SideNavigation
        open={at1280 && mobileNavOpen}
        menus={NAV_MENUS}
        setMobileNavOpen={(val) => {
          setMobileNavOpen(val);
        }}
        toggleTheme={toggleTheme}
      />
      <WalletSidebar
        open={walletSidebarOpen}
        setWalletSidebarOpen={setWalletSidebarOpen}
        handleSettings={() => {
          setWalletSidebarOpen(false);
          setSettingMenu(true);
          setMobileNavOpen(false);
        }}
      />

      <Root top={fixedTopPosition}>
        <Container path={pathname.includes('/marketplace') ? 1 : 0}>
          <MobileMenuBox>
            <SideNavigationButton onClick={handleCloseSideModals}>
              {!mobileNavOpen && !walletSidebarOpen ? <MenuIcon /> : <CloseIcon />}
            </SideNavigationButton>
            <GMILogo logoColor={theme.palette.text.main} />
          </MobileMenuBox>

          <Menu>
            {NAVBAR_MENUS.map((item, index) =>
              item.submenus ? (
                <SubMenu
                  key={index}
                  index={index}
                  name={item.text}
                  path={item.path}
                  menus={item.submenus}
                  active={isActiveSubMenu(item.submenus)}
                />
              ) : item.text === 'Get GMI' ? (
                <GetGMIButton
                  key={index}
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    goPath(item.path);
                  }}
                >
                  {item.text}
                </GetGMIButton>
              ) : (
                <CustomMenuButton
                  key={index}
                  href={item.path}
                  onClick={(e) => {
                    e.preventDefault();
                    goPath(item.path);
                  }}
                  active={(item.tabs
                    ? isActiveTab(item.tabs)
                    : pathname + search === item.path
                  ).toString()}
                  className={
                    item.text === 'Airdrop' ? 'airdrop' : item.text === 'LP Bonds' ? 'lp-bonds' : ''
                  }
                >
                  {item.text}
                </CustomMenuButton>
              ),
            )}
          </Menu>
          {below600 ? (
            <>
              <IconButton onClick={() => setMobileSearch(true)} sx={{ marginLeft: 6 }}>
                <SearchIcon sx={{ fontSize: 30 }} />
              </IconButton>

              <SearchDrawer open={mobileSearch} onClose={() => setMobileSearch(false)} />
            </>
          ) : (
            <SearchFieldContainer ref={searchContainerRef}>
              <SearchTextField
                placeholder="Search"
                value={searchText}
                icon={SearchIcon}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" style={{ color: theme.palette.icon.tertiary }}>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                onChange={(e) => {
                  handleSearchInput(e.target.value);
                }}
                onFocus={() => setIsInputFocused(true)}
              />
              {(showSearchResultContainer || isInputFocused) && (
                <SearchResultContainer
                  isLoading={isLoading}
                  searchResult={searchResult}
                  setSearchText={setSearchText}
                  onClose={() => {
                    setSearchText('');
                    setSearchResult({});
                    setIsInputFocused(false);
                  }}
                  isError={error}
                  isInputFocused={isInputFocused}
                />
              )}
            </SearchFieldContainer>
          )}

          {/* TODO: Enable modes when all components supports it */}
          {/* {!below905 &&
            (theme.palette.mode === 'dark' ? (
              <ToolTip title="Switch to Light theme" placement="top-end">
                <IconContainer onClick={toggleTheme}>
                  <LightIcon />
                </IconContainer>
              </ToolTip>
            ) : (
              <ToolTip title="Switch to Dark theme" placement="top-end">
                <IconContainer onClick={toggleTheme}>
                  <DarkIcon />
                </IconContainer>
              </ToolTip>
            ))} */}
          <HeaderCtaBtnContainer>
            <ToggleContainer>
              <NetworkSelect />
            </ToggleContainer>
            <AddressButton openWalletSideBar={() => setWalletSidebarOpen(!walletSidebarOpen)} />
          </HeaderCtaBtnContainer>
        </Container>
      </Root>
    </>
  );
};

export default Header;
